import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../services/api';
import Pagination from '../../components/common/Pagination';
import { useTranslation } from '../../context/LanguageContext';

function GlobalUsersPage() {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [companies, setCompanies] = useState([]);

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    useEffect(() => {
        fetchUsers();
    }, [page, size, filter]);

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${getApiBaseUrl()}/platform/companies?size=1000`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (result.success && result.data) {
                const companyNames = [...new Set((result.data.content || []).map(c => c.companyName))];
                setCompanies(companyNames);
            }
        } catch (error) {
            console.error('회사 목록 조회 실패:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            let url = `${getApiBaseUrl()}/platform/users?page=${page}&size=${size}`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (result.success && result.data) {
                const data = result.data;
                let content = data.content || [];

                if (filter) {
                    content = content.filter(u => u.companyName === filter);
                }

                setUsers(content);
                setTotalElements(data.totalElements || 0);
                setTotalPages(data.totalPages || 0);
            }
        } catch (error) {
            console.error('사용자 목록 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString('ko-KR');
    };

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'SUPER_ADMIN': return 'active';
            case 'ADMIN': return 'info';
            default: return '';
        }
    };

    const getRoleLabel = (role) => {
        switch (role) {
            case 'SUPER_ADMIN': return t('pages.platform.globalUsers.role.superAdmin');
            case 'ADMIN': return t('pages.platform.globalUsers.role.admin');
            case 'USER': return t('pages.platform.globalUsers.role.user');
            default: return role;
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    const handleFilterChange = (e) => {
        setFilter(e.target.value);
        setPage(0);
    };

    return (
        <div className="platform-page">

            {/* 통계 */}
            <div className="platform-stats-grid">
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">group</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">{totalElements}</div>
                        <div className="platform-stat-label">{t('pages.platform.globalUsers.stats.total')}</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">admin_panel_settings</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {users.filter(u => u.role === 'SUPER_ADMIN' || u.role === 'ADMIN').length}
                        </div>
                        <div className="platform-stat-label">{t('pages.platform.globalUsers.stats.admin')}</div>
                    </div>
                </div>
                <div className="platform-stat-card">
                    <div className="platform-stat-icon">
                        <span className="material-symbols-outlined">check_circle</span>
                    </div>
                    <div className="platform-stat-info">
                        <div className="platform-stat-value">
                            {users.filter(u => u.status === 'ACTIVE').length}
                        </div>
                        <div className="platform-stat-label">{t('pages.platform.globalUsers.stats.active')}</div>
                    </div>
                </div>
            </div>

            {/* 사용자 목록 */}
            <div className="platform-table-container">
                <div className="platform-table-header">
                    <h3 className="platform-table-title">{t('pages.platform.globalUsers.listTitle')}</h3>
                    <div className="platform-table-tools">
                        <select
                            className="platform-select"
                            value={filter}
                            onChange={handleFilterChange}
                        >
                            <option value="">{t('pages.platform.globalUsers.filterAllCompanies')}</option>
                            {companies.map(company => (
                                <option key={company} value={company}>{company}</option>
                            ))}
                        </select>
                    </div>
                </div>
                {loading ? (
                    <div className="platform-empty">
                        <div className="platform-empty-text">{t('pages.platform.globalUsers.loading')}</div>
                    </div>
                ) : users.length === 0 ? (
                    <div className="platform-empty">
                        <span className="material-symbols-outlined platform-empty-icon">group</span>
                        <div className="platform-empty-text">{t('pages.platform.globalUsers.empty')}</div>
                    </div>
                ) : (
                    <>
                        <div className="platform-table-scroll">
                        <table className="platform-table">
                            <thead>
                                <tr>
                                    <th>{t('pages.platform.globalUsers.col.name')}</th>
                                    <th>{t('pages.platform.globalUsers.col.email')}</th>
                                    <th>{t('pages.platform.globalUsers.col.company')}</th>
                                    <th>{t('pages.platform.globalUsers.col.role')}</th>
                                    <th>{t('pages.platform.globalUsers.col.status')}</th>
                                    <th>{t('pages.platform.globalUsers.col.lastLogin')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td><strong>{user.username}</strong></td>
                                        <td>{user.email}</td>
                                        <td>{user.companyName}</td>
                                        <td>
                                            <span className={`platform-badge ${getRoleBadgeClass(user.role)}`}>
                                                {getRoleLabel(user.role)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`platform-badge ${user.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                                                {user.status === 'ACTIVE'
                                                    ? t('pages.platform.globalUsers.statusActive')
                                                    : t('pages.platform.globalUsers.statusInactive')}
                                            </span>
                                        </td>
                                        <td>{formatDate(user.lastLoginAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            size={size}
                            onPageChange={handlePageChange}
                            onSizeChange={handleSizeChange}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default GlobalUsersPage;
